import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com';

let initialized = false;

/**
 * Initialize PostHog analytics
 * Call once on app startup (main.tsx)
 */
export function initAnalytics() {
  if (initialized) return;
  
  if (!POSTHOG_KEY) {
    console.log('[Analytics] PostHog key not configured - events will be logged to console');
    initialized = true;
    return;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: true,
      capture_pageleave: true,
      persistence: 'localStorage',
      autocapture: false, // We'll track custom events explicitly
    });
    initialized = true;
    console.log('[Analytics] PostHog initialized');
    
    // Temporary test event to verify integration
    posthog.capture('bc_install_test');
    console.log('[Analytics] Test event bc_install_test fired');
  } catch (error) {
    console.error('[Analytics] Failed to initialize PostHog:', error);
  }
}

/**
 * Identify a user for analytics tracking
 * Call after login/signup to link events to the user
 */
export function identify(userId: string, traits?: Record<string, any>) {
  if (!POSTHOG_KEY) {
    console.log('[Analytics] identify:', { userId, traits });
    return;
  }

  try {
    posthog.identify(userId, traits);
  } catch (error) {
    console.error('[Analytics] Failed to identify user:', error);
  }
}

/**
 * Track a custom event
 */
export function track(event: string, properties?: Record<string, any>) {
  if (!POSTHOG_KEY) {
    console.log('[Analytics] track:', event, properties);
    return;
  }

  try {
    posthog.capture(event, properties);
  } catch (error) {
    console.error('[Analytics] Failed to track event:', error);
  }
}

/**
 * Reset the current user's identity (call on logout)
 */
export function resetAnalytics() {
  if (!POSTHOG_KEY) {
    console.log('[Analytics] reset');
    return;
  }

  try {
    posthog.reset();
  } catch (error) {
    console.error('[Analytics] Failed to reset:', error);
  }
}

/**
 * Set user properties without tracking an event
 */
export function setUserProperties(properties: Record<string, any>) {
  if (!POSTHOG_KEY) {
    console.log('[Analytics] setUserProperties:', properties);
    return;
  }

  try {
    posthog.people.set(properties);
  } catch (error) {
    console.error('[Analytics] Failed to set user properties:', error);
  }
}

// Export posthog instance for advanced usage if needed
export { posthog };
